import React, { forwardRef } from 'react';
import { Box, BoxProps } from '@mui/material';
import { Swiper, SwiperProps } from 'swiper/react';
import { Keyboard, EffectFade, Autoplay, Parallax } from 'swiper/modules';

export interface CarouselProps
  // `cssMode` not supported
  // instead of `navigation` use <CarouselNavigation />
  // instead of `pagination` use <CarouselPagination />
  // instead of `onSwiper` use `onInit`
  extends
    Omit<SwiperProps, 'children' | 'cssMode' | 'navigation' | 'pagination' | 'onSwiper'>,
    Omit<BoxProps, keyof Omit<SwiperProps, 'children'> | 'component'> {
  /**
   * Callback that receives Swiper instance
   */
  onInit?: SwiperProps['onSwiper'];
  /**
   * Travelled distance in px for peek effect
   * @default 100
   */
  peekDistance?: number;
  /**
   * Speed of peek effect
   * @default 150 (half of default Carousel's speed)
   */
  peekSpeed?: number;
}

/**
 * Modern mobile friendly carousel for slideshows, built with Swiper
 */
export const Carousel: React.FC<CarouselProps> = forwardRef(
  ({ children, sx, onInit, effect, autoplay, parallax, peekDistance = 100, peekSpeed = 150, ...rest }, ref) => {
    return (
      <Box
        component={Swiper}
        spaceBetween={0}
        autoHeight
        loop
        keyboard={{
          enabled: true,
        }}
        modules={[
          Keyboard,
          ...(effect === 'fade' ? [EffectFade] : []),
          ...(autoplay ? [Autoplay] : []),
          ...(parallax ? [Parallax] : []),
        ]}
        parallax={parallax}
        effect={effect}
        autoplay={autoplay}
        ref={ref}
        {...rest}
        onSwiper={onInit}
        data-peekspeed={peekSpeed}
        sx={{
          // generic root styling, based on the `import 'swiper/css'`
          display: 'block',
          ml: 'auto',
          mr: 'auto',
          p: '0',
          maxWidth: '100%',
          // ensure correct position
          position: 'relative',
          zIndex: (theme) => theme.zIndex.carousel,
          overflow: 'hidden',
          // support for ul-type
          listStyle: 'none',
          // variants
          '&.swiper-horizontal': {
            touchAction: 'pan-y',
          },
          '&.swiper-vertical': {
            touchAction: 'pan-x',
            '& > .swiper-wrapper': {
              flexDirection: 'column',
            },
          },
          '&.swiper-autoheight': {
            height: 'auto',
            '& > .swiper-wrapper': {
              alignItems: 'flex-start',
              transitionProperty: 'transform, height',
            },
          },
          // wrapper of all slides
          '& .swiper-wrapper': {
            position: 'relative',
            width: '100%',
            height: '100%',
            zIndex: (theme) => theme.zIndex.carousel,
            display: 'flex',
            transitionProperty: 'transform',
            transitionTimingFunction: 'var(--swiper-wrapper-transition-timing-function, initial)',
            boxSizing: 'content-box',
            transform: 'translate3d(0px, 0, 0)',
          },
          // peek effect
          ...(effect !== 'fade' && {
            '&.peeking .swiper-wrapper > .swiper-slide': {
              transitionProperty: 'transform',
              transitionDuration: `${peekSpeed}ms`,
              transitionTimingFunction: 'var(--swiper-wrapper-transition-timing-function, initial)',
            },
            '&.peek-next .swiper-wrapper > .swiper-slide': {
              transform: (theme) => `translate3D(${theme.direction === 'rtl' ? '' : '-'}${peekDistance}px, 0, 0)`,
            },
            '&.peek-prev .swiper-wrapper > .swiper-slide': {
              transform: (theme) => `translate3D(${theme.direction === 'rtl' ? '-' : ''}${peekDistance}px, 0, 0)`,
            },
          }),
          // swiper internal
          '.swiper-slide-invisible-blank': {
            visibility: 'hidden',
          },
          '.swiper-backface-hidden .swiper-slide': {
            transform: 'translateZ(0)',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
          },
          // does not include CSS Mode, 3D Shadows, lazy loading
          ...sx,
        }}
      >
        {children}
      </Box>
    );
  },
);
